import { FC, useContext, useState, useMemo, useEffect, JSX } from "react";
import { RouteComponentProps } from "react-router-dom";
import { ColumnConfig, SortDirections, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { TemplateService } from "@app/admin/manage-templates";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { ServicingCountTypeEnum, ServicingMinimumTypeEnum } from "@app/templates/templateModels";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { AccessCriteriaModel, CategoryModel, ServicingCriteriaModel, ServicingDetailsModel, SpecialtyComment, SpecialtyCommentActionsEnum, SpecialtyGroupModel, SpecialtyModel } from "@app/admin/manage-specialties/specialty";
import { CodeMapSetService } from "@app/codemaps";
import { CodeMapTypeEnum } from "@app/codemaps/CodeMapSetModels";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { UserContext } from "@app/user/UserContext";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import Roles from "@app/user/roles";
import GeographyService from "@app/geography/geographyService";
import { ClientListItem } from "@app/client/client";
import SpecialtyService from "@app/admin/manage-specialties/specialtyService";
import CreateNewSpecialtyPanelContent from "@app/admin/manage-specialties/CreateNewSpecialtyPanelContent";
import { TemplateDetailsData } from "@app/admin/manage-specialties/templateDetailsData";

interface AddSpecialtyGroupTableData {
  deleteBtn: JSX.Element;
  specialtyCode: string;
  specialtyGroupCode: string;
  edit: JSX.Element;
  [wafflesKeyColumnName]: string;
}

interface SpecialtyCodeLineItem {
  id: number,
  specialtyCode: string,
  specialtyCodeName: string,
  specialtyCodeProviderWeight: string,
}

interface TaxonomySpecialtyCodeLineItem {
  id: number,
  taxonomyCode: string,
  taxonomySpecialtyCode: string,
}

/* oxlint-disable sort-keys */
const specialtyDetailsColumnConfig: ColumnConfig = {
  specialtyGroupCode: {
    displayText: "Specialty Group",
    sizeContentToFit: true,
    testId: "addSpecialty-specialtyGroupCode",
  },
  specialtyCode: {
    displayText: "Specialty Code",
    sizeContentToFit: true,
    testId: "addSpecialty-specialtyCode",
  },
  edit: {
    displayText: "Edit Specialty",
    sizeContentToFit: true,
    testId: "addSpecialty-editSpecialty",
  },
  deleteBtn: {
    displayText: "Remove",
    sizeContentToFit: true,
    testId: "addSpecialty-deleteBtn",
  },
};

/* oxlint-enable sort-keys */
interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<AddSpecialtyGroupTableData>,
}

interface AddSpecialtyMatchProps {
  clientId?: string;
  templateCollectionId?: string;
  templateId?: string;
}

type AddSpecialtyProps = RouteComponentProps<AddSpecialtyMatchProps, undefined>;

export const AddSpecialty: FC<React.PropsWithChildren<AddSpecialtyProps>> = (props: AddSpecialtyProps) => {
  const specialtyService = new SpecialtyService();
  const codeMapSetService = new CodeMapSetService();
  const geographyService = new GeographyService();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const user = useContext(UserContext);

  const [isQuestAdmin] = useState(user.roles.includes(Roles.QUEST_ADMIN));

  const [sidePanelLoading, setSidePanelLoading] = useState(false);
  const [specialtyGroups, setSpecialtyGroups] = useState<AddSpecialtyGroupTableData[]>([]);
  const [rawSpecialtyGroupRows, setRawSpecialtyGroupRows] = useState<SpecialtyGroupModel[]>([]);
  const [templateDetails, setTemplateDetails] = useState<TemplateDetailsData>();
  const [newTemplateDetails, setNewTemplateDetails] = useState<TemplateDetailsData>();
  const [classificationCodeMapSets, setClassificationCodeMapSets] = useState<JSX.Element[]>([])
  const [selectedClassificationCodeMapSet, setSelectedClassificationCodeMapSet] = useState(0)
  const [accessCodeMapMismatch, setAccessCodeMapMismatch] = useState(false);
  const [counties, setCounties] = useState<Set<string>>();

  const [newSpecialtiesComments, setNewSpecialtiesComments] = useState<SpecialtyComment[]>([]);

  const [take] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSettings, setPageSettings] = useState<PageSettings>(
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      },
    });

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take }),
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: totalRecords,
  }

  const breadcrumbs: (string | Breadcrumb)[] = useMemo(() => {
    const breadcrumbs: (string | Breadcrumb)[] = [];

    breadcrumbs.push({
      link: `/admin/manage-templates/manage-specialties/specialties/${props.match.params.templateCollectionId}/${props.match.params.templateId}`,
      text: 'Manage Specialties'
    });
    breadcrumbs.push('Add Specialties');

    return breadcrumbs;
  }, [props.location.state]);

  useEffect(() => {
    const fetchData = async () => {
      setTemplateDetails(await specialtyService.getTemplateDetails(Number(props.match.params.templateCollectionId), props.match.params.templateId));
      setCounties(new Set((await geographyService.getCounties()).map(county => county.countySSA)));

      const classificationOptions: JSX.Element[] = [<option key={0} value={0}></option>];
      const classificationMaps = await codeMapSetService.get(CodeMapTypeEnum.CountyClassMap);
      setClassificationCodeMapSets(classificationOptions.concat(classificationMaps?.map(cm => {
        return (<option key={cm.codeMapSetId} value={cm.resource.resourceId}>{cm.resource.name}</option>)
      })));
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (rawSpecialtyGroupRows.length <= 0) {
      setSpecialtyGroups([]);
    }
    else {
      setSpecialtyGroups(createSpecialtyGroupWafflesTable(rawSpecialtyGroupRows));
    }
  }, [rawSpecialtyGroupRows, newTemplateDetails]);

  const deleteNewSpecialtyGroup = (index: number) => {
    const newSpecialtiesList = rawSpecialtyGroupRows;
    const removedSpecialtyName = newSpecialtiesList[index].name;
    setRawSpecialtyGroupRows(newSpecialtiesList.filter((_, i) => i !== index));
    setNewSpecialtiesComments(newSpecialtiesComments => newSpecialtiesComments.filter(comment => comment.specialtyName !== removedSpecialtyName));
  }

  const mapSpecialties = (specialtyCodeLineItems: SpecialtyCodeLineItem[]) => {
    return specialtyCodeLineItems.map(scli => {
      const sm: SpecialtyModel = {
        code: scli.specialtyCode,
        displayOrder: 0,
        name: scli.specialtyCodeName,
        weight: scli.specialtyCodeProviderWeight,
      }
      return sm;
    });
  }

  const mapSpecialtyGroup = (specialtyGroupCode: string, specialtyGroupCodeName: string, isProvider: boolean, newSpecialties: SpecialtyModel[], specialtyAccessCriteria: AccessCriteriaModel[], informational: boolean, expandable: boolean, countDistinct: ServicingCountTypeEnum, specialtyServicingCriteria: ServicingCriteriaModel[], usingRatios: boolean, taxonomyCodesCount: number, servicingDetails: ServicingDetailsModel[], customizationDetails: ServicingDetailsModel[], providerCount: number) => {
    return {
      accessCriteria: specialtyAccessCriteria,
      analysisProperties: informational ? "informationalonly" : "none",
      code: specialtyGroupCode,
      customizationDetails,
      displayOrder: 0,
      displayProperties: expandable ? "canexpandspecialties" : "none",
      enabled: false,
      entityType: isProvider ? "provider" : "facility",
      isNameValid: true,
      isSelected: false,
      isValid: true,
      name: specialtyGroupCodeName,
      providerCount,
      servicingCountBy: countDistinct,
      servicingCriteria: specialtyServicingCriteria,
      servicingDetails,
      servicingMinimumType: usingRatios ? ServicingMinimumTypeEnum.Ratio : ServicingMinimumTypeEnum.MinimumProviders,
      specialties: newSpecialties,
      taxonomyCodesCount: taxonomyCodesCount,
    }
  }

  const handleSpecialtyEdit = (accessReqInCounty: boolean,
    categoryDisplayName: string,
    creditPercentage: number,
    expandable: boolean,
    isProvider: boolean,
    loading: boolean,
    informational: boolean,
    selectedTaxonomyCrosswalk: string,
    servicingReqInCounty: boolean,
    specialtyAccessCriteria: AccessCriteriaModel[],
    specialtyServicingCriteria: ServicingCriteriaModel[],
    specialtyCode: string,
    specialtyCodeLineItems: SpecialtyCodeLineItem[],
    specialtyCodeName: string,
    specialtyCodeProviderWeight: string,
    specialtyGroupCode: string,
    specialtyGroupCodeName: string,
    taxonomyCode: string,
    taxonomyCrosswalks: JSX.Element[],
    taxonomySpecialtyCode: string,
    taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[],
    telehealthCredit: boolean,
    usingRatios: boolean,
    countDistinct: ServicingCountTypeEnum,
    minimumServicingCounts: number,
    providerCount: number,
    index?: number,
    customizationDetails?: ServicingDetailsModel[],
    servicingDetails?: ServicingDetailsModel[],
  ) => {

    const newSpecialtyGroupRows = rawSpecialtyGroupRows;
    const newSpecialties: SpecialtyModel[] = mapSpecialties(specialtyCodeLineItems);

    specialtyAccessCriteria.forEach(x => {
      x.isTelehealthAccessCreditCustomization = telehealthCredit;
    })

    const newRow: SpecialtyGroupModel =
      mapSpecialtyGroup(specialtyGroupCode, specialtyGroupCodeName, isProvider, newSpecialties, specialtyAccessCriteria, informational, expandable, countDistinct, specialtyServicingCriteria, usingRatios, taxonomySpecialtyCodeLineItems.length,  servicingDetails, customizationDetails, providerCount);
    const newCategories: CategoryModel[] = templateDetails.categories;
     if (categoryDisplayName != "" && categoryDisplayName != undefined && categoryDisplayName != null) {
      newCategories.find((x) => x.displayName === categoryDisplayName).specialtyGroups[newSpecialtyGroupRows[index].displayOrder] = newRow;
     }
    else {
      newCategories[0].specialtyGroups[newSpecialtyGroupRows[index].displayOrder] = newRow;
    }
    newRow.displayOrder = newSpecialtyGroupRows[index].displayOrder;
    newSpecialtyGroupRows[index] = newRow;
    setRawSpecialtyGroupRows(newSpecialtyGroupRows);
    setNewTemplateDetails({
      accessCodeMapSetId: templateDetails.accessCodeMapSetId,
      accessCodeMapSetVersionId: templateDetails.accessCodeMapSetVersionId,
      accessCriteriaSpecificationGranularity: templateDetails.accessCriteriaSpecificationGranularity,
      attestationCrosswalkCodeMapSetId: parseInt(selectedTaxonomyCrosswalk),
      categories: newCategories,
      closestProviderCount: providerCount,
      correlationId: templateDetails.correlationId,
      createdByUserId: templateDetails.createdByUserId,
      creationComment: templateDetails.creationComment,
      displayName: templateDetails.displayName,
      memberSetId: templateDetails.memberSetId,
      motiveCrosswalkCodeMapSetId: templateDetails.motiveCrosswalkCodeMapSetId,
      servicingAnalysisGranularity: templateDetails.servicingAnalysisGranularity,
      servicingAnalysisType: templateDetails.servicingAnalysisType, //servicingReqInCounty ? ServicingAnalysisTypeEnum.InRegion : ServicingAnalysisTypeEnum.InRegionOrCoversMember,
      skipServicingAnalysis: templateDetails.skipServicingAnalysis,
      templateCollectionId: templateDetails.templateCollectionId,
      templateDescription: templateDetails.templateDescription,
      templateId: templateDetails.templateId,
      timeDistanceCalculatorTypes: templateDetails.timeDistanceCalculatorTypes,
      useMedicareRatebook: templateDetails.useMedicareRatebook,
      zipClassType: templateDetails.zipClassType,
    })

    setSpecialtyGroups(createSpecialtyGroupWafflesTable(rawSpecialtyGroupRows));
    setSidePanelLoading(false);
    setTotalRecords(newSpecialtyGroupRows.length);
  }

  const handleSpecialtyCreate = (
    accessReqInCounty: boolean,
    categoryDisplayName: string,
    creditPercentage: number,
    expandable: boolean,
    isProvider: boolean,
    loading: boolean,
    informational: boolean,
    selectedTaxonomyCrosswalk: string,
    servicingReqInCounty: boolean,
    specialtyAccessCriteria: AccessCriteriaModel[],
    specialtyServicingCriteria: ServicingCriteriaModel[],
    specialtyCode: string,
    specialtyCodeLineItems: SpecialtyCodeLineItem[],
    specialtyCodeName: string,
    specialtyCodeProviderWeight: string,
    specialtyGroupCode: string,
    specialtyGroupCodeName: string,
    taxonomyCode: string,
    taxonomyCrosswalks: JSX.Element[],
    taxonomySpecialtyCode: string,
    taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[],
    telehealthCredit: boolean,
    usingRatios: boolean,

    countDistinct: ServicingCountTypeEnum,
    minimumServicingCounts: number,
    providerCount: number,
    index?: number,
    customizationDetails?: ServicingDetailsModel[],
    servicingDetails?: ServicingDetailsModel[],
  ) => {
    const newSpecialtyGroupRows = rawSpecialtyGroupRows;
    const newSpecialties: SpecialtyModel[] = mapSpecialties(specialtyCodeLineItems);

    specialtyAccessCriteria.forEach(x => {
      x.isTelehealthAccessCreditCustomization = telehealthCredit;
    })

    const newRow: SpecialtyGroupModel =
      mapSpecialtyGroup(specialtyGroupCode, specialtyGroupCodeName, isProvider, newSpecialties, specialtyAccessCriteria, informational, expandable, countDistinct, specialtyServicingCriteria, usingRatios, taxonomySpecialtyCodeLineItems.length, servicingDetails, customizationDetails, providerCount);

    const newCategories: CategoryModel[] = templateDetails.categories;
    if (categoryDisplayName != "" && categoryDisplayName != undefined && categoryDisplayName != null) {
      newCategories.find(function (category) {
        // +2 because displayorder numbering is 1 based
        newRow.displayOrder = category.specialtyGroups.length + 2;
        return category.displayName === categoryDisplayName;
      }).specialtyGroups.push(newRow);
    }
    else {
      // +2 because displayorder numbering is 1 based
      newRow.displayOrder = newCategories[0].specialtyGroups.length + 2;
      newCategories[0].specialtyGroups.push(newRow);
    }

    newSpecialtyGroupRows.push(newRow);
    const updatedNewSpecialties = [...newSpecialtiesComments, {actionTaken: SpecialtyCommentActionsEnum.Added, specialtyName: newRow.name}]
    setNewSpecialtiesComments(updatedNewSpecialties)
    setRawSpecialtyGroupRows(newSpecialtyGroupRows);
    setNewTemplateDetails({
      accessCodeMapSetId: templateDetails.accessCodeMapSetId,
      accessCodeMapSetVersionId: templateDetails.accessCodeMapSetVersionId,
      accessCriteriaSpecificationGranularity: templateDetails.accessCriteriaSpecificationGranularity,
      attestationCrosswalkCodeMapSetId: parseInt(selectedTaxonomyCrosswalk),
      categories: newCategories,
      closestProviderCount: providerCount,
      correlationId: templateDetails.correlationId,
      createdByUserId: user.userId,
      creationComment: JSON.stringify(updatedNewSpecialties),
      displayName: templateDetails.displayName,
      memberSetId: templateDetails.memberSetId,
      motiveCrosswalkCodeMapSetId: templateDetails.motiveCrosswalkCodeMapSetId,
      servicingAnalysisGranularity: templateDetails.servicingAnalysisGranularity,
      servicingAnalysisType: templateDetails.servicingAnalysisType,
      skipServicingAnalysis: templateDetails.skipServicingAnalysis,
      templateCollectionId: templateDetails.templateCollectionId,
      templateDescription: templateDetails.templateDescription,
      templateId: templateDetails.templateId,
      timeDistanceCalculatorTypes: templateDetails.timeDistanceCalculatorTypes,
      useMedicareRatebook: templateDetails.useMedicareRatebook,
      zipClassType: templateDetails.zipClassType
    })
    /* oxlint-enable sort-keys */
    setSpecialtyGroups(createSpecialtyGroupWafflesTable(rawSpecialtyGroupRows));
    setSidePanelLoading(false);
    setTotalRecords(newSpecialtyGroupRows.length);
  }
  /* oxlint-enable */

  const handleSave = async () => {
    const templateService: TemplateService = new TemplateService();
    await templateService.addTemplateJson(newTemplateDetails).then(() =>
      {
        window.location.href = "/app/v2.0/admin/manage-templates";
      }
    );
    setSpecialtyGroups(createSpecialtyGroupWafflesTable([]));
    setNewTemplateDetails(undefined);
  }

  const handleCancel = async () => {
    setRawSpecialtyGroupRows([]);
    setSpecialtyGroups(createSpecialtyGroupWafflesTable([]));
    setNewTemplateDetails(undefined);
  }

  const handleSelectCodeMapSet = async (codeMapSetId: number) => {
    const codeMapItems = await codeMapSetService.getCodeMapItems(codeMapSetId);
    const codeMapSSAs = new Set(codeMapItems.map(cmi => cmi.sourceValue));
    const codeMapClassifications = new Set(codeMapItems.map(cmi => cmi.codeMapValue));
    const specialtyGroups = templateDetails?.categories?.flatMap(sg => sg.specialtyGroups);
    const specialtyGroupsData = new Set([...specialtyGroups[0]?.accessCriteria?.map(x => x.geographyId), ...specialtyGroups[0]?.servicingCriteria?.map(x => x.geographyId)]);
    const templateClassifications = new Set([...specialtyGroupsData].filter(criteria => !counties.has(criteria)));
    const templateGeographyIds = [...specialtyGroupsData].filter(criteria => !templateClassifications.has(criteria));

    // For geoIds, we want the business to be able to select only mappings that have at least all ids outlined in the template
    const geoIdMismatch = ![...templateGeographyIds].every((geoId) => codeMapSSAs.has(geoId));
    // For classifications, we do not want the business to be able to select any mapping that doesn't match the template exactly
    const classificationMismatch = !(templateClassifications.size === codeMapClassifications.size &&
      [...templateClassifications].every((classification) => codeMapClassifications.has(classification)));

    setAccessCodeMapMismatch(geoIdMismatch || classificationMismatch);
    setSelectedClassificationCodeMapSet(codeMapSetId);
  }

  const createSpecialtyGroupWafflesTable = (data: SpecialtyGroupModel[]): AddSpecialtyGroupTableData[] => {
    return data.map((rec, index) => {
      /* oxlint-disable sort-keys */
      return ({
        key: rec.code,
        specialtyGroupCode: rec.name,
        specialtyCode: rec.code,
        taxonomyCodesCount: rec.taxonomyCodesCount,
        edit: (
          <FlyoutLink
            content={{
              body: <CreateNewSpecialtyPanelContent
                currentUserid={user.userId}
                defaultClient={{ clientId: user.clientId, name: user.clientName } as ClientListItem}
                allowClientEdit={isQuestAdmin && !props.match.params.clientId}
                handleSave={handleSpecialtyEdit}
                countyClassificationMap={selectedClassificationCodeMapSet}
                templateDetails={newTemplateDetails}
                specialtyGroup={rec}
                loading={sidePanelLoading}
                index={index}
                features={featureFlags}
                servicingDetails={rec.servicingDetails}
                customizationDetails={rec.customizationDetails}
                providerCount={rec.providerCount}
                />,
              title: "Edit Specialty",
            }}
            testId="edit-specialty__edit-group-flyout"
            classNames="qes-button-secondary"
          >
            <QesIcon iconName={FontAwesome.Edit} description="Edit specialty" />
            </FlyoutLink>),
        deleteBtn: (
          <ModalLink
            classNames="qes-button-secondary danger"
            content={{
              body: "Are you sure you want to delete this specialty group?",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => deleteNewSpecialtyGroup(index),
              confirmButtonText: "Delete",
              confirmButtonType: "primary-danger",
              title: `Delete Specialty Group`,
            }}
            testId="manage-specialties-list__deleteBtn-new-specialty-group-modal"
            hideBodyScroll={true}
          >
            Delete
          </ModalLink>)
      });
      /* oxlint-enable sort-keys */
    });
  }

  const handleAddSpecialtyGroupSort = (data: AddSpecialtyGroupTableData[], newSortedColumn: keyof AddSpecialtyGroupTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  return (
    <>
      <Page documentTitle="Manage Specialties - Admin">
        <Breadcrumbs
          template={!!props.match.params.clientId ? BreadcrumbTemplateEnum.ClientAdministration : BreadcrumbTemplateEnum.Admin}
          items={breadcrumbs}
        />
        <PageInner>
          <PageHeader title="Specialty Details" iconName={FontAwesome.Folder} />
          <PageSection>
            {!!newTemplateDetails && specialtyGroups.length > 0 &&
              (
                <div className="row mt-3">
                  <div className="col-12" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                    <QESButton
                      qesType="secondary"
                      qesTestId="add-specialtyGroup__save-button"
                      onClick={() => { handleSave() }}
                    >
                      Save
                  </QESButton>
                  <ModalLink
                    content={{
                      body: <>Remove all new Specialty Groups and Specialties?</>,
                      cancelButtonText: "Cancel",
                      confirmButtonAction: () => handleCancel(),
                      confirmButtonText: "Delete",
                      confirmButtonType: "primary-danger",
                      title: "Delete New Specialties",
                    }}
                    testId="add-specialtyGroup__cancel-button"
                    classNames="qes-button-secondary danger"
                    hideBodyScroll={true}
                  >
                    Cancel
                  </ModalLink>
                  </div>
                </div>
              )
            }
            <div className="row mt-3">
              <div className="col-12" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }}>
                <div style={{ minWidth: "100%", paddingLeft: "3%", paddingRight: "2%" }}>
                  <Waffles<AddSpecialtyGroupTableData>
                    columns={specialtyDetailsColumnConfig}
                    customSort={handleAddSpecialtyGroupSort}
                    data={specialtyGroups}
                    emptyTableMessage=""
                    enablePagination={true}
                    fullWidth={true}
                    passThroughSorting={true}
                    paginationProps={paginationProps}
                    testId="client-manage-specialtyCollections-waffles-table" />
                </div>
              </div>
            </div>
            <div style={{ paddingTop: '5px' }}>
              { accessCodeMapMismatch && (
                <QESAlert type={"danger"} persist={true}>
                  <span>{"The selected code map set contains classifications that are not present in the current template."}</span>
                </QESAlert>)
              }
              <QESSelect
                testId="create-specialty__taxonomy-selection"
                containerClassName="p-2 w-25 mb-2 bd-highlight"
                label="County to Classification Code Map"
                required={true}
                onChange={(e) => {
                  handleSelectCodeMapSet(Number(e.target.value));
                }}
              >
                {Object.values(classificationCodeMapSets)}
              </QESSelect>
              <FlyoutLink
                content={{
                  body: <CreateNewSpecialtyPanelContent
                    currentUserid={user.userId}
                    defaultClient={{ clientId: user.clientId, name: user.clientName } as ClientListItem}
                    allowClientEdit={isQuestAdmin && !props.match.params.clientId}
                    handleSave={handleSpecialtyCreate}
                    countyClassificationMap={selectedClassificationCodeMapSet}
                    templateDetails={templateDetails}
                    loading={sidePanelLoading}
                    features={featureFlags}
                    />,
                  title: "Add New Specialty",
                }}
                disabled={!(selectedClassificationCodeMapSet > 0 && !accessCodeMapMismatch)}
                testId="create-specialty__create-group-flyout"
                classNames="qes-button-primary"
              >
                Add New Specialty
              </FlyoutLink>
            </div>
          </PageSection>
        </PageInner>
      </Page >
    </>
  );
}